// Connector grid card with brand monogram, glowing health dot, sync sparkline,
// last-sync stamp, error-container styling when unhealthy.

import { Sparkline } from '../charts/Sparkline.jsx';

const BRAND_TONES = {
  servicenow: { mono: 'SN', accent: '#62d84e', foreground: '#00152c' },
  salesforce: { mono: 'SF', accent: '#00a1e0', foreground: '#00152c' },
  pmrt: { mono: 'PM', accent: '#1961a1', foreground: '#ffffff' },
  jira: { mono: 'J', accent: '#2684ff', foreground: '#00152c' },
  confluence: { mono: 'C', accent: '#172b4d', foreground: '#ffffff' },
  vision: { mono: 'VS', accent: '#0a66c2', foreground: '#ffffff' },
  sharepoint: { mono: 'SP', accent: '#036c70', foreground: '#ffffff' },
  bedrock: { mono: 'B', accent: '#ff9900', foreground: '#00152c' },
  esaar: { mono: 'eS', accent: '#5a8c66', foreground: '#00152c' },
  finance: { mono: '$', accent: '#5a8c66', foreground: '#00152c' },
};

function brandFor(connector) {
  const key = String(
    connector.sourceSystem || connector.system || connector.id || '',
  ).toLowerCase();
  for (const [k, v] of Object.entries(BRAND_TONES)) {
    if (key.includes(k)) return v;
  }
  return {
    mono: (connector.name || 'CN').slice(0, 2).toUpperCase(),
    accent: '#1961a1',
    foreground: '#ffffff',
  };
}

function healthTone(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('warn') || s.includes('latency') || s.includes('degrad')) return 'amber';
  if (s.includes('error') || s.includes('fail')) return 'brick';
  if (s.includes('healthy') || s === 'ok' || s === 'connected') return 'sage';
  return 'slate';
}

function relTime(iso) {
  if (!iso) return 'never';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.round(diff))}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function humanize(value) {
  return String(value || 'not set')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function ConnectorCard({ connector, syncHistory = [], onSync, onOpen, busy }) {
  const brand = brandFor(connector);
  const tone = healthTone(connector.status);
  const cls = `connector-card connector-${tone}`;
  return (
    <article className={cls}>
      <header className="connector-head">
        <span
          className="connector-monogram"
          style={{ background: brand.accent, color: brand.foreground }}
          aria-hidden
        >
          {brand.mono}
        </span>
        <div className="stack tight" style={{ flex: 1 }}>
          {/* The name is the card's primary affordance — testers click it first
              (IMP-001: the footer button alone was too easy to miss). */}
          {onOpen ? (
            <button
              type="button"
              className="connector-title-link"
              onClick={() => onOpen(connector)}
            >
              {connector.name}
            </button>
          ) : (
            <strong>{connector.name}</strong>
          )}
          <span className="muted small">
            {connector.sourceSystem || connector.system || connector.id}
          </span>
        </div>
        <span className={`connector-health-dot tone-${tone}`} aria-hidden />
      </header>
      <div className="connector-meta">
        <span
          className={`chip ${tone === 'sage' ? 'sage' : tone === 'amber' ? 'amber' : tone === 'brick' ? 'red' : 'outline'} sm`}
        >
          {(() => {
            const s = String(connector.status || 'unknown');
            return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
          })()}
        </span>
        <span className="muted small">Last simulation {relTime(connector.lastSyncAt)}</span>
      </div>
      <div className="connector-meta" aria-label="Prototype adapter classification">
        <span className="chip blue sm">Prototype adapter</span>
        <span className="muted small">{humanize(connector.maturityLevel)}</span>
        <span className="muted small">{humanize(connector.environmentMode)}</span>
      </div>
      {syncHistory.length ? (
        <div className="connector-sparkline-wrap">
          <Sparkline
            values={syncHistory}
            width={220}
            height={32}
            tone={tone === 'brick' ? 'brick' : tone === 'amber' ? 'amber' : 'sage'}
          />
          <span className="muted small">
            Projected records · last {syncHistory.length} simulation
            {syncHistory.length === 1 ? '' : 's'}
          </span>
        </div>
      ) : null}
      <footer className="connector-actions">
        <button className="btn secondary sm" onClick={() => onOpen && onOpen(connector)}>
          Open connector
        </button>
        <button
          className="btn primary sm tactile-press"
          onClick={() => onSync && onSync(connector)}
          disabled={busy}
        >
          Simulate sync
        </button>
      </footer>
    </article>
  );
}
